"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { GROQButton } from "@/sanity/lib/definitions";
import { stegaClean } from "@sanity/client/stega";
import { Slot } from "@radix-ui/react-slot";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { iconMap } from "@/lib/iconMap";

const buttonVariants = cva(
  "button cta button-size-default button-shape-default",
  {
    variants: {
      variant: {
        default: "button-default",
        white: "button-white",
        whiteFaded: "button-white-faded",
      },
      action: {
        link: "",
        popup: "",
        anchor: "",
        none: "",
      },
    },
    defaultVariants: {
      variant: "default",
      action: "none",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = "default", action, asChild = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const children = props.children;
    return (
      <Comp
        className={cn("group", buttonVariants({ variant, className }))}
        data-action={action}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export interface CTAButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  data: GROQButton;
  overrideText?: string;
  iconOverride?: keyof typeof iconMap;
  buttonClassName?: string;
  fullWidth?: boolean;
  /** Keep the icon box below 640px too (hidden there by default). */
  showIconOnMobile?: boolean;
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  ({ className, buttonClassName, data, overrideText, iconOverride, fullWidth = false, showIconOnMobile = false, ...props }, ref) => {
    const cleanedData = stegaClean(data ?? {}) ?? null;

    const hasIcon = iconOverride ?? cleanedData?.cta?.icon ?? "none";
    const icon = iconMap[hasIcon as keyof typeof iconMap];

    const action = cleanedData?.cta?.action ?? null;

    const buttonText =
      overrideText ??
      (data?.cta
        ? (data.cta.title ?? "Please add a title")
        : "Please select a CTA");

    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const [iconSize, setIconSize] = React.useState<number | null>(null);

    React.useEffect(() => {
      const el = buttonRef.current;
      if (!el || !icon) return;

      const observer = new ResizeObserver(([entry]) => {
        setIconSize(
          entry.contentRect.height +
            parseFloat(getComputedStyle(el).paddingTop) * 2,
        );
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, [icon]);

    const hasIconRendered = Boolean(icon && hasIcon && hasIcon !== "none");

    const wrapperStyle =
      hasIconRendered && iconSize
        ? ({ "--cta-icon-size": `${iconSize}px` } as React.CSSProperties)
        : undefined;

    return (
      <CTAWrapper
        cta={cleanedData?.cta}
        className={cn(
          "cta-wrapper relative z-10 inline-flex items-center w-full",
          fullWidth ? "sm:w-full md:w-full" : "sm:w-auto",
          hasIconRendered && "has-icon",
          showIconOnMobile && "cta-icon-mobile",
          className,
        )}
        style={wrapperStyle}
      >
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 pointer-events-none cta-bg z-[1] rounded-[1.25rem] bg-[#98D8FF]"
        />

        <Button
          className={cn(
            "cta-button relative z-[2] flex items-center w-full h-auto group !bg-transparent",
            fullWidth ? "lg:!w-full" : "lg:w-fit",
            buttonClassName,
          )}
          action={action ?? "none"}
          ref={(node) => {
            (
              buttonRef as React.MutableRefObject<HTMLButtonElement | null>
            ).current = node;
            if (typeof ref === "function") ref(node);
            else if (ref)
              (
                ref as React.MutableRefObject<HTMLButtonElement | null>
              ).current = node;
          }}
          {...props}
        >
          <div className="relative button-text overflow-clip">
            <div className="transition-all duration-300 pr-[.5rem] button-text-initial">
              {buttonText}
            </div>
            <div className="absolute transition-all duration-300 pointer-events-none button-text-hover top-[3rem] pr-[.5rem]">
              {buttonText}
            </div>
          </div>
        </Button>

        {hasIconRendered && (
          <div
            className={`button-icon-box button-shape-default relative z-[2] ml-[.5rem] shrink-0 ${showIconOnMobile ? "flex" : "hidden sm:flex"} items-center justify-center overflow-clip bg-[#98D8FF] text-black [&_svg]:w-[3rem] [&_svg]:h-[3rem] [&_svg]:shrink-0`}
            style={iconSize ? { width: iconSize, height: iconSize } : undefined}
          >
            <div className="flex justify-center items-center w-full h-full button-icon rotate-[-45deg]">
              {icon}
            </div>
          </div>
        )}
      </CTAWrapper>
    );
  },
);
CTAButton.displayName = "CTAButton";

export { Button, buttonVariants, CTAButton };
